import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, KeyRound, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { orgApi, saveOrganizerToken } from "../lib/organizerApi";
import { useOrganizerAuth } from "../auth/OrganizerAuthContext";
import PasswordStrengthMeter, { evaluatePassword } from "../components/PasswordStrengthMeter";
import PasswordInput from "../components/PasswordInput";

/**
 * Reset-password — Phase 6.5 / Module 2.
 *
 * Public page that consumes a one-time token from `?token=...` and lets the
 * user set a new password. On success the backend returns a fresh JWT
 * (every prior session is revoked via `password_version`).
 */
export default function OrganizerResetPassword() {
  const navigate = useNavigate();
  const { refresh } = useOrganizerAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const strength = evaluatePassword(pw);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Liên kết không hợp lệ. Vui lòng yêu cầu liên kết mới."); return; }
    if (pw.length < 8) { setError("Mật khẩu phải có ít nhất 8 ký tự."); return; }
    if (pw !== pw2) { setError("Hai mật khẩu không khớp. Vui lòng kiểm tra lại."); return; }
    if (!strength.ok) { setError("Mật khẩu quá yếu. Vui lòng dùng hỗn hợp chữ hoa, chữ thường, số và ký tự đặc biệt."); return; }

    setBusy(true);
    try {
      const { data } = await orgApi.post("/organizer/reset-password", { token, new_password: pw });
      saveOrganizerToken(data.access_token);
      await refresh();
      setDone(true);
      setTimeout(() => navigate("/organizer/dashboard", { replace: true }), 1600);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) setError("Liên kết không hợp lệ. Vui lòng yêu cầu liên kết mới.");
      else if (status === 409) setError("Liên kết đã được sử dụng. Vui lòng yêu cầu liên kết mới nếu cần.");
      else if (status === 410) setError("Liên kết đã hết hạn. Vui lòng yêu cầu liên kết mới.");
      else setError(typeof detail === "string" ? detail : "Đặt lại mật khẩu thất bại. Vui lòng thử lại.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center px-4 py-12" data-testid="organizer-reset-page">
        <div className="w-full max-w-md bg-white border border-espresso/10 rounded-3xl p-8 text-center" data-testid="reset-invalid">
          <AlertCircle className="w-10 h-10 text-espresso-soft mx-auto" />
          <h1 className="mt-3 text-2xl font-serif text-espresso">Liên kết không hợp lệ</h1>
          <p className="mt-2 text-sm text-espresso-soft">
            Liên kết đặt lại mật khẩu đã thiếu hoặc sai. Vui lòng yêu cầu liên kết mới.
          </p>
          <Link
            to="/organizer/forgot-password"
            data-testid="reset-request-new"
            className="mt-5 inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            Gửi liên kết mới
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-4 py-12" data-testid="organizer-reset-page">
      <div className="w-full max-w-md">
        <Link to="/organizer/login" className="inline-flex items-center gap-1.5 text-espresso-soft hover:text-espresso text-sm">
          <ArrowLeft className="w-3.5 h-3.5" />
          Quay lại đăng nhập
        </Link>

        <div className="mt-6 bg-white border border-espresso/10 rounded-3xl p-7 sm:p-9 shadow-sm">
          <div className="flex items-center gap-2 text-espresso-soft text-xs uppercase tracking-[0.28em]">
            <Sparkles className="w-3.5 h-3.5" /> Đặt lại mật khẩu
          </div>
          <h1 className="mt-2 text-3xl font-serif text-espresso">Tạo mật khẩu mới</h1>

          {done ? (
            <div data-testid="reset-done" className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto" />
              <div className="mt-2 font-semibold text-emerald-900">Mật khẩu đã được cập nhật</div>
              <p className="mt-1.5 text-sm text-emerald-800">
                Đang chuyển bạn vào bảng điều khiển…
              </p>
            </div>
          ) : (
            <>
              <p className="mt-2 text-espresso-soft text-sm">
                Tạo mật khẩu mới cho tài khoản tổ chức của bạn. Mọi phiên đăng nhập trên các thiết bị khác sẽ bị thu hồi.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">Mật khẩu mới</span>
                  <PasswordInput
                    data-testid="reset-password"
                    testIdSuffix="reset-password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    minLength={8}
                    required
                    placeholder="Tối thiểu 8 ký tự"
                    autoComplete="new-password"
                    wrapperClassName="mt-1"
                    leftIcon={null}
                    className="w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
                  />
                  <PasswordStrengthMeter password={pw} testIdPrefix="reset-strength" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">Nhập lại mật khẩu</span>
                  <PasswordInput
                    data-testid="reset-password-confirm"
                    testIdSuffix="reset-password-confirm"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    minLength={8}
                    required
                    placeholder="Nhập lại mật khẩu"
                    autoComplete="new-password"
                    wrapperClassName="mt-1"
                    leftIcon={null}
                    className="w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
                  />
                </label>

                {error && (
                  <div data-testid="reset-error" className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy || !pw || !pw2}
                  data-testid="reset-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3 text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  {busy ? "Đang lưu…" : "Đặt lại mật khẩu"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
